import type { OpacityModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import OpacityIcon from "@svg/modifiers/opacity-icon";

import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { ChevronDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

import { OPACITY_OPTIONS } from "../constants";

interface OpacityModifierProps {
  onChange: (modifier: OpacityModifierValue) => void;
}

const OpacityModifier: React.FC<OpacityModifierProps> = ({ onChange }) => {
  const currentValue = useOptimisticModifierValue({
    modifierType: { type: "opacity", value: 1 },
    extractor: (elementDesignation) => {
      return elementDesignation.element.styleOverrides?.opacity ?? null;
    },
  });

  const mapDecimalToPercentage = (decimal: number): number => {
    return Math.round(decimal * 100);
  };

  const displayValue =
    currentValue != null ? `${mapDecimalToPercentage(currentValue)}%` : "100%";

  const handleValueChange = (opacity: number) => {
    onChange({
      type: "opacity",
      value: opacity,
    });
  };

  return (
    <LengthInputSelector
      field="opacity"
      value={displayValue}
      startEnhancer={<OpacityIcon className="w-3 h-3" />}
      onChange={(newValue) => {
        const result = parseUnit(
          newValue,
          { value: 100, unit: "%" },
          "default",
          "%",
        );
        const numericValue =
          typeof result.value === "number"
            ? result.value
            : Number.parseFloat(result.value);
        if (
          !Number.isNaN(numericValue) &&
          numericValue >= 0 &&
          numericValue <= 100
        ) {
          handleValueChange(numericValue / 100);
        }
      }}
      placeholder="100%"
      metrics={["%"]}
      unitDefaults={{ value: "100", unit: "%" }}
      menuOptions={OPACITY_OPTIONS}
      minValues={{ "%": 0 }}
      maxValues={{ "%": 100 }}
      inputClassName="w-full"
      endEnhancer={() => <ChevronDown size={12} />}
    />
  );
};

export default OpacityModifier;
